import type { OidcClientSettings } from 'oidc-client-ts'
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts'
import GlobalPrototypes from '@/config/services/globalPrototypes.ts'
import sentryConfig from '@/store/sentry.js'
import native from 'src/config/native.js'
import type { StoredOidcUser } from './types.ts'
import { AUTH_TYPE } from '@/config/constants.ts'

export class KeycloakAuth {
  private static instance: KeycloakAuth | undefined
  private static readonly realmRegex = /\/realms\/([^/]+)/
  private static userStore: WebStorageStateStore | undefined
  private static readonly BROWSER_SESSION_TOKENS_KEY = 'kc-browser-session-tokens'
  private static readonly REFRESH_LOCK_PREFIX = 'sdui:keycloak-refresh:'
  private static pendingRefresh: Promise<User | null> | null = null

  private readonly oidcConfig: OidcClientSettings
  private readonly userManager: UserManager
  private unregisterUserManagerEvents?: () => void

  private readonly keycloakBaseUrl: string
  private readonly clientId: string
  private readonly redirectUri: string
  private readonly postLogoutRedirectUri: string
  private readonly responseType: string
  private readonly scope: string
  private readonly realm: string

  /**
   * Gets or creates the shared WebStorageStateStore instance.
   */
  private static getUserStore(): WebStorageStateStore | undefined {
    if (KeycloakAuth.userStore) {
      return KeycloakAuth.userStore
    }

    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.getItem('') // Test access
        KeycloakAuth.userStore = new WebStorageStateStore({ store: window.localStorage })
        return KeycloakAuth.userStore
      }
    } catch {
      console.info('Local storage is not available')
    }

    return undefined
  }

  private constructor(realm: string) {
    this.realm = realm
    this.keycloakBaseUrl = import.meta.env.VITE_KEYCLOAK_BASE_URL
    this.clientId = import.meta.env.VITE_CLIENT_ID
    this.responseType = import.meta.env.VITE_RESPONSE_TYPE || 'code'
    this.scope = import.meta.env.VITE_SCOPE || 'openid profile email offline_access'

    this.postLogoutRedirectUri = this.determinePostLogoutRedirectUri()
    this.redirectUri = this.determineRedirectUri()

    if (!this.keycloakBaseUrl || !this.clientId || !this.redirectUri || !this.postLogoutRedirectUri) {
      sentryConfig.throwError(`
        Missing required environment variables for Keycloak authentication:
        keycloakBaseUrl: ${this.keycloakBaseUrl}
        clientId: ${this.clientId}
        redirectUri: ${this.redirectUri}
        postLogoutRedirectUri: ${this.postLogoutRedirectUri}
      `)
    }

    this.oidcConfig = {
      authority: this.getEncodedOidcAuthority(realm),
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      response_type: this.responseType,
      scope: this.scope,
      post_logout_redirect_uri: this.postLogoutRedirectUri,
      loadUserInfo: true,
    }
    const userStore = KeycloakAuth.getUserStore()

    this.userManager = new UserManager({
      ...this.oidcConfig,
      ...(userStore ? { userStore } : {}),
      // Prevents oidc-client-ts's SilentRenewService from calling userManager.signinSilent()
      // directly, bypassing our pendingRefresh dedup. Renewal is handled via the
      // accessTokenExpiring listener in registerUserManagerEvents() instead.
      automaticSilentRenew: false,
    })

    this.registerUserManagerEvents()
  }

  /**
   * Determines the redirect URI based on platform (web or native iOS/Android).
   */
  private determineRedirectUri(): string {
    if (!native.isNative()) {
      return import.meta.env.VITE_REDIRECT_URI
    }

    return native.isIOS()
      ? import.meta.env.VITE_MOBILE_REDIRECT_URL_IOS
      : import.meta.env.VITE_MOBILE_REDIRECT_URL_ANDROID
  }

  /**
   * Determines the post-logout redirect URI based on platform.
   * Native apps use the same URI as redirect URI.
   */
  private determinePostLogoutRedirectUri(): string {
    if (!native.isNative()) {
      return import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI
    }

    return native.isIOS()
      ? import.meta.env.VITE_MOBILE_REDIRECT_URL_IOS
      : import.meta.env.VITE_MOBILE_REDIRECT_URL_ANDROID
  }

  /**
   * Registers event handlers on the UserManager instance to handle token refresh.
   * Updates the Vuex store with new access tokens when they are refreshed.
   * Also syncs refresh tokens to multi_users for same-realm multi-account support.
   */
  private registerUserManagerEvents(): void {
    this.unregisterUserManagerEvents?.()

    const onUserLoaded = (user: User) => {
      if (!GlobalPrototypes.store) return

      if (user.access_token) {
        GlobalPrototypes.store.commit('SET_AUTH_TOKEN', {
          stayLoggedIn: true,
          authToken: user.access_token
        })
      }

      this.syncRefreshTokenToMultiUsers(user)
    }

    const onAccessTokenExpiring = async () => {
      await this.signinSilent()
    }

    this.userManager.events.addUserLoaded(onUserLoaded)
    this.userManager.events.addAccessTokenExpiring(onAccessTokenExpiring)

    this.unregisterUserManagerEvents = () => {
      this.userManager.events.removeUserLoaded(onUserLoaded)
      this.userManager.events.removeAccessTokenExpiring(onAccessTokenExpiring)
    }
  }

  /**
   * Syncs the refresh token from the OIDC user to the multi_users store.
   * Uses profile.sub (Keycloak subject claim) to identify the correct stored user,
   * avoiding race conditions when switching between accounts.
   */
  private syncRefreshTokenToMultiUsers(user: User): void {
    if (!user.refresh_token || !user.profile?.sub || !GlobalPrototypes.store) return

    const storedUsers = GlobalPrototypes.store.state.multiUsers?.users ?? {}
    const matchingUser = Object.values(storedUsers)
      .find(u => u.keycloakSub === user.profile.sub && u.authType === AUTH_TYPE.KEYCLOAK)

    if (!matchingUser) return

    GlobalPrototypes.store.commit('multiUsers/SAVE_USER_REFRESH_TOKEN', {
      userId: matchingUser.id,
      refreshToken: user.refresh_token
    })
  }

  /**
   * Extracts realm from an authenticated user's session.
   */
  private static async getRealmFromUserSession(keys: string[]): Promise<string | undefined> {
    const userKey = keys.find(k => k.startsWith('user:'))
    if (!userKey || !KeycloakAuth.userStore) return

    const data = await KeycloakAuth.userStore.get(userKey)
    if (!data) return

    try {
      const user = User.fromStorageString(data)
      const match = user?.profile?.iss?.match(KeycloakAuth.realmRegex)
      return match?.[1]
    } catch (e) {
      sentryConfig.throwError(`getRealmFromUserSession: ${e}`)
    }

    return
  }

  /**
   * Extracts realm from signin state (mid-login flow).
   */
  private static async getRealmFromSigninState(keys: string[]): Promise<string | undefined> {
    const stateKey = keys.find(k => !k.startsWith('user:'))
    if (!stateKey || !KeycloakAuth.userStore) return

    const data = await KeycloakAuth.userStore.get(stateKey)
    if (!data) return

    try {
      const stateObj = JSON.parse(data)
      const match = stateObj?.authority?.match(KeycloakAuth.realmRegex)
      return match?.[1]
    } catch (e) {
      sentryConfig.throwError(`getRealmFromSigninState: ${e}`)
      return
    }
  }

  /**
   * Realm of the current singleton instance 
   */
  public static getInstanceRealm(): string | undefined {
    return KeycloakAuth.instance?.realm
  }

  public getPostLogoutRedirectUri(): string {
    return this.postLogoutRedirectUri
  }

  /**
   * Saves the id_token from a browser login flow, indexed by realm.
   */
  public static saveBrowserSessionToken(realm: string, idToken: string): void {
    try {
      const stored = JSON.parse(localStorage?.getItem(KeycloakAuth.BROWSER_SESSION_TOKENS_KEY) || '{}')
      stored[realm] = idToken
      localStorage?.setItem(KeycloakAuth.BROWSER_SESSION_TOKENS_KEY, JSON.stringify(stored))
    } catch { 
      // If localStorage is unavailable we continue without it
    }
  }

  /**
   * Gets the browser session id_token for a specific realm.
   */
  public static getBrowserSessionToken(realm: string): string | undefined {
    try {
      const stored = JSON.parse(localStorage?.getItem(KeycloakAuth.BROWSER_SESSION_TOKENS_KEY) || '{}')
      return stored[realm]
    } catch {
      return undefined
    }
  }

  /**
   * Builds a Keycloak end_session_endpoint URL for clearing browser session cookies
   */
  public buildSessionClearUrl(realm: string, idToken: string, postLogoutUri: string): string {
    const authority = this.getEncodedOidcAuthority(realm)
    const params = new URLSearchParams({
      id_token_hint: idToken,
      post_logout_redirect_uri: postLogoutUri,
      client_id: this.clientId,
    })
    return `${authority}protocol/openid-connect/logout?${params.toString()}`
  }

  /**
   * Get realm from local storage without needing an instance.
   */
  public static async getStoredRealm(): Promise<string | undefined> {
    const store = KeycloakAuth.getUserStore()
    if (!store) return

    const keys = await store.getAllKeys()
    return await KeycloakAuth.getRealmFromUserSession(keys)
      ?? await KeycloakAuth.getRealmFromSigninState(keys)
  }

  public getEncodedOidcAuthority(realm: string): string {
    const encodedRealm = encodeURIComponent(realm)
    return `${this.keycloakBaseUrl}${encodedRealm}/`
  }

  public async logout({ idToken, slink }: { idToken: string; slink?: string }): Promise<void> {
    if (native.isNative()) {
      return this.userManager.signoutRedirect({
        post_logout_redirect_uri: this.postLogoutRedirectUri,
        id_token_hint: idToken
      })
    }

    const baseUri = this.postLogoutRedirectUri.replace(/\/$/, '') // Remove trailing slash
    const redirectUri = slink ? `${baseUri}/${slink}/login` : this.postLogoutRedirectUri

    return this.userManager.signoutRedirect({
      post_logout_redirect_uri: redirectUri,
      id_token_hint: idToken
    })
  }

  public async signinRedirectCallback(): Promise<User> {
    return this.userManager.signinRedirectCallback()
  }

  public async signinRedirect(options?: { prompt?: string }): Promise<void> {
    await this.userManager.signinRedirect(options)
  }

  /**
   * Redirects to Keycloak requesting step-up authentication (acr_values=2) and forces the user to enter their
   * credentials again (prompt=login), even if they just logged in. The username is passed as login_hint so
   * Keycloak pre-fills it and the user only has to type the password. `returnPath` is restored after the OIDC callback.
   */
  public async signinRedirectStepUp(returnPath: string): Promise<void> {
    const profile = (await this.getUser())?.profile
    const loginHint = profile?.preferred_username ?? profile?.email

    await this.userManager.signinRedirect({
      prompt: 'login',
      extraQueryParams: { acr_values: '2' },
      state: { returnPath },
      ...(loginHint ? { login_hint: loginHint } : {}),
    })
  }

  public async getUser(): Promise<User | null> {
    return this.userManager.getUser()
  }

  public async signinSilent(): Promise<User | null> {
    if (KeycloakAuth.pendingRefresh) {
      return KeycloakAuth.pendingRefresh
    }

    KeycloakAuth.pendingRefresh = (async () => {
      try {
        return await this.refreshWithCrossTabLock()
      } finally {
        KeycloakAuth.pendingRefresh = null
      }
    })()

    return KeycloakAuth.pendingRefresh
  }

  /**
   * Performs the silent token refresh coordinated across browser tabs sharing
   * the same `localStorage` (and therefore the same Keycloak refresh_token).
   *
   * Uses the Web Locks API so only one tab at a time talks to Keycloak. Inside
   * the lock, double-checks against storage: if another tab already rotated the
   * token while this tab was waiting, the fresh user is reused instead of
   * sending the now-stale refresh_token to Keycloak (which would be rejected
   * as "Max reuse exceeded" and invalidate the entire session for all tabs).
   *
   * Falls back to a plain refresh when `navigator.locks` is unavailable.
   */
  private async refreshWithCrossTabLock(): Promise<User | null> {
    const userBeforeLock = await this.userManager.getUser().catch(() => null)
    // null user means no current session; default to 0 so any valid token in
    // storage is treated as "fresher" and reused without calling Keycloak.
    const expiresAtBefore = userBeforeLock?.expires_at ?? 0

    const doRefresh = async (): Promise<User | null> => {
      const userInStorage = await this.userManager.getUser().catch(() => null)
      if (
        userInStorage?.expires_at &&
        userInStorage.expires_at > expiresAtBefore &&
        !userInStorage.expired
      ) {
        // Fire userLoaded so Vuex/Axios are updated with the fresh token that
        // another tab already wrote to storage — skipping signinSilent() means
        // the event would not fire otherwise.
        await this.userManager.events.load(userInStorage)
        return userInStorage
      }

      try {
        return await this.userManager.signinSilent()
      } catch (e) {
        sentryConfig.throwError(`signinSilent: ${e}`)
        return null
      }
    }

    const locks = typeof navigator === 'undefined' ? undefined : navigator.locks
    if (locks) {
      return locks
        .request(
          `${KeycloakAuth.REFRESH_LOCK_PREFIX}${this.realm}`,
          { mode: 'exclusive', signal: AbortSignal.timeout(15_000) },
          doRefresh
        )
        .catch((e: unknown) => {
          sentryConfig.throwError(`keycloak refresh lock timed out or failed: ${e}`)
          return doRefresh()
        })
    }

    return doRefresh()
  }

  /**
   * Stores a User object in the UserManager and performs a silent token refresh.
   *
   * @returns Promise resolving to the refreshed User
   * @throws Error if silent refresh fails
   */
  private async storeAndRefreshUser(user: User): Promise<User> {
    await this.userManager.storeUser(user)

    const refreshedUser = await this.signinSilent()

    if (!refreshedUser) {
      throw new Error('Session refresh failed')
    }

    return refreshedUser
  }

  /**
   * Restores a user session from stored OIDC data.
   *
   * @param oidcUserData - Stored OIDC user data from multi_users module
   * @returns Promise resolving to the refreshed User
   * @throws Error if refresh_token is missing or silent refresh fails
   */
  public async restoreUser(oidcUserData: StoredOidcUser): Promise<User> {
    if (!oidcUserData.refresh_token) {
      throw new Error('No refresh token available')
    }

    const user = new User({
      access_token: oidcUserData.access_token,
      token_type: oidcUserData.token_type,
      id_token: oidcUserData.id_token,
      refresh_token: oidcUserData.refresh_token,
      expires_at: oidcUserData.expires_at,
      scope: oidcUserData.scope || this.scope,
      profile: oidcUserData.profile,
    })

    return this.storeAndRefreshUser(user)
  }

  /**
   * Restores a user session from a refresh token.
   * Creates a minimal User object with the refresh token and refreshes it.
   *
   * @param refreshToken - The refresh token to use for session restoration
   * @returns Promise resolving to the refreshed User with fresh tokens
   * @throws Error if refresh_token is missing or silent refresh fails
   */
  public async restoreUserWithToken(refreshToken: string): Promise<User> {
    if (!refreshToken) {
      throw new Error('No refresh token provided')
    }

    const user = new User({
      refresh_token: refreshToken,
      access_token: '',
      token_type: 'Bearer',
      profile: { sub: '', iss: '', aud: '', exp: 0, iat: 0 },
      scope: this.scope,
    })

    return this.storeAndRefreshUser(user)
  }

  public getOidcConfig(): OidcClientSettings {
    return this.oidcConfig
  }

  /**
   * Cleans up the instance by removing event listeners and clearing user session.
   */
  private async cleanUpInstance(): Promise<void> {
    this.unregisterUserManagerEvents?.()
    this.unregisterUserManagerEvents = undefined
    // Prevent clearing pendingRefresh while a token refresh POST is in-flight 
    if (KeycloakAuth.pendingRefresh) {
      await KeycloakAuth.pendingRefresh.catch(() => {})
    }
    KeycloakAuth.pendingRefresh = null
  }

  /**
   * Gets or creates the singleton instance.
   *
   * If called with a realm that differs from the current instance's realm,
   * the existing instance will be destroyed and a new one created with the new realm.
   *
   * @param realm - The Keycloak realm to authenticate against. Required when creating a new instance.
   * @returns The singleton instance, or undefined if no instance exists and no realm provided.
   */
  public static async getInstance(realm?: string): Promise<KeycloakAuth | undefined> {
    // No instance exists
    if (!KeycloakAuth.instance) {
      if (!realm) return undefined
      KeycloakAuth.instance = new KeycloakAuth(realm)
      return KeycloakAuth.instance
    }

    // Return existing instance if no realm specified or same realm
    if (!realm || KeycloakAuth.instance.realm === realm) {
      return KeycloakAuth.instance
    }

    // Realm changed - clean up and recreate
    await KeycloakAuth.instance.cleanUpInstance()
    KeycloakAuth.instance = new KeycloakAuth(realm)
    return KeycloakAuth.instance
  }

  /**
   * Clears all OIDC-related data from storage.
   */
  public static async clearStorage(): Promise<void> {
    const store = KeycloakAuth.getUserStore()
    if (store) {
      const keys = await store.getAllKeys()
      await Promise.all(keys.map(key => store.remove(key)))
    }

    localStorage?.removeItem('kc-login-in-progress')
    localStorage?.removeItem(KeycloakAuth.BROWSER_SESSION_TOKENS_KEY)

    if (KeycloakAuth.instance) {
      await KeycloakAuth.instance.cleanUpInstance()
      KeycloakAuth.instance = undefined
    }
  }

  /**
   * Clears OIDC storage keys belonging to a specific realm.
   * Used for single-account logout to clean up only that user's session data.
   *
   * @param realm - The realm to clear storage for
   */
  public async clearStorageForRealm(realm: string): Promise<void> {
    const store = KeycloakAuth.getUserStore()
    if (!store) return

    const keys = await store.getAllKeys()
    const encodedRealm = encodeURIComponent(realm)

    const realmKeys = keys.filter(key => key.includes(encodedRealm))

    await Promise.all(realmKeys.map(key => store.remove(key)))

    try {
      const stored = JSON.parse(localStorage?.getItem(KeycloakAuth.BROWSER_SESSION_TOKENS_KEY) || '{}')
      delete stored[realm]
      localStorage?.setItem(KeycloakAuth.BROWSER_SESSION_TOKENS_KEY, JSON.stringify(stored))
    } catch {
      // ignore localStorage errors
    }
  }

  /**
   * Revokes a specific refresh token directly via the Keycloak token revocation endpoint (RFC 7009).
   * Bypasses UserManager to ensure the correct token is revoked, not the active session's token.
   *
   * @param realm - The Keycloak realm
   * @param refreshToken - The refresh token to revoke
   * @throws Error if the revocation request fails
   */
  private async revokeRefreshToken(realm: string, refreshToken: string): Promise<void> {
    const authority = this.getEncodedOidcAuthority(realm)
    const url = `${authority}protocol/openid-connect/revoke`

    const body = new URLSearchParams({
      token: refreshToken,
      token_type_hint: 'refresh_token',
      client_id: this.clientId,
    })

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    })

    if (!response.ok) {
      throw new Error(`Token revocation failed with status ${response.status}`)
    }
  }

  /**
   * Revokes tokens and clears storage for a specific user session.
   * Used for single-account logout in multi-account scenarios.
   *
   * Token revocation is fire-and-forget - errors are logged but don't block logout.
   *
   * @param realm - The realm to clear storage for
   * @param refreshToken - If provided, triggers server-side token revocation for this specific token
   */
  public async revokeUserSession(realm: string, refreshToken?: string): Promise<void> {
    if (refreshToken) {
      try {
        await this.revokeRefreshToken(realm, refreshToken)
      } catch (e) {
        sentryConfig.addBreadcrumb({
          category: 'auth/revokeUserSession',
          message: `Token revocation failed: ${e}`,
          level: 'warning'
        })
      }
    }

    await this.clearStorageForRealm(realm)
  }
}
